export type TrafficDataStatus =
  | 'AVAILABLE'
  | 'PARTIAL'
  | 'NO_DATA'
  | 'NO_CURRENT_TRAFFIC_REFERENCES';

export interface TrafficSamplePoint {
  timestamp: string;
  nanoseconds?: number;
  value: number;
}

export interface TrafficNoDataWindow {
  metric: string;
  source: string;
  from: string;
  to: string;
}

export interface Last24hEndpoint {
  endpointKey: string | null;
  dataStatus: TrafficDataStatus;
  series: {
    uploadBps: TrafficSamplePoint[];
    downloadBps: TrafficSamplePoint[];
  };
  noDataWindows?: TrafficNoDataWindow[];
}

export interface Last24hResponse {
  schemaVersion: string;
  queryAsOf?: string;
  window?: { from: string; to: string; interval: string };
  unit: string;
  dataStatus: TrafficDataStatus;
  endpoints: Last24hEndpoint[];
}

export interface DirectionalDailyMetric {
  avgBps: number | null;
  maxBps: number | null;
  p95Bps: number | null;
  observationCount?: number;
  historyObservationCount?: number;
  provenance?: string | null;
}

export interface DailyPeriod {
  date: string;
  dataStatus: TrafficDataStatus;
  metrics?: {
    upload: DirectionalDailyMetric;
    download: DirectionalDailyMetric;
  };
  sampleCount?: number;
  expectedSampleCount?: number;
  coverage?: number | null;
  uploadBytes?: number | null;
  downloadBytes?: number | null;
  totalBytes?: number | null;
  volumeCompleteness?: string | null;
  uploadVolumeSource?: string | null;
  downloadVolumeSource?: string | null;
}

export interface DailyEndpoint {
  endpointKey: string | null;
  dataStatus: TrafficDataStatus;
  periods: DailyPeriod[];
}

export interface DailyResponse {
  schemaVersion: string;
  unit: string;
  dataStatus: TrafficDataStatus;
  endpoints: DailyEndpoint[];
}

export interface DirectionalMonthlyMetric {
  avgBps: number | null;
  maxBps: number | null;
  p95Bps?: number | null;
  bytes?: number | null;
}

export interface MonthlyPeriod {
  month: string;
  periodStatus: 'PARTIAL' | 'COMPLETE';
  dataStatus: TrafficDataStatus;
  eligibleDays?: number;
  calendarDays?: number;
  sampleCount?: number;
  expectedSampleCount?: number;
  coverage?: number | null;
  uploadBytes?: number | null;
  downloadBytes?: number | null;
  totalBytes?: number | null;
  volumeStatus?: string | null;
  volumeDaysKnown?: number | null;
  volumeDaysNone?: number | null;
  volumeDaysPreV2?: number | null;
  volumeDaysLegacyRate?: number | null;
  metrics?: {
    upload: DirectionalMonthlyMetric;
    download: DirectionalMonthlyMetric;
  };
}

export interface MonthlyEndpoint {
  endpointKey: string | null;
  dataStatus: TrafficDataStatus;
  periods: MonthlyPeriod[];
}

export interface MonthlyResponse {
  schemaVersion: string;
  unit: string;
  dataStatus: TrafficDataStatus;
  endpoints: MonthlyEndpoint[];
}

export interface InstitutionContextLocality {
  ubigeoCode: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
}

export interface InstitutionContextPlan {
  downloadMbps: number | null;
  uploadMbps: number | null;
}

export interface InstitutionContext {
  clientServiceId: number;
  modularCode: string | null;
  institutionName: string | null;
  ugel: string | null;
  ugelRuc?: string | null;
  routerIp?: string | null;
  locality: InstitutionContextLocality | null;
  contractedPlan: InstitutionContextPlan | null;
}

export interface InstitutionalServiceContext extends InstitutionContext {
  status: string | null;
}

export interface EndpointOption {
  key: string | null;
  label: string;
}

export interface PanelError {
  httpStatus: number | null;
  code: string | null;
  message: string;
  retryable: boolean;
}

export type PanelState = 'loading' | 'ready' | 'error';

export interface MonthlyVolumeKpis {
  month: string;
  periodStatus: 'PARTIAL' | 'COMPLETE';
  volumeStatus: string | null;
  periodLabel: string;
  downloadBytes: number | null;
  uploadBytes: number | null;
  totalBytes: number | null;
  isVolumePartial: boolean;
  downloadMaxMbps: number | null;
  uploadMaxMbps: number | null;
}

export const AVAILABILITY_PLACEHOLDER = {
  percent: 100,
  caption: 'Sin incidencias registradas',
} as const;
